import { useEffect, useState, FormEvent, useRef } from "react";
import { api } from "../api/client.js";
import { useAuth } from "../context/AuthContext.js";

interface Product {
  id: string; title: string; category: string; targetGender: string;
  targetTags: string[]; tagMatchMode: "all" | "any";
  sourceUrl?: string; status: string; revision: number; createdAt: string;
}
interface ProductImage { assetId: string; downloadUrl: string; }
interface AffiliateLink {
  id: string; influencer_id: string; influencer_name: string;
  affiliate_url: string | null; available: boolean;
}
interface Influencer { id: string; name: string; tags: string[]; }

const GENDER_LABEL: Record<string, string> = { female: "Mujer", male: "Hombre", unisex: "Unisex" };
const GENDER_COLOR: Record<string, string> = { female: "#d946ef", male: "#3b82f6", unisex: "#6b7280" };
const CATEGORY_OPTIONS = ["ropa", "calzado", "complementos", "joyería", "bolsos", "belleza", "hogar", "deporte", "otro"];

const emptyForm = { title: "", category: "ropa", targetGender: "unisex", sourceUrl: "", targetTags: [] as string[], tagMatchMode: "any" as "all" | "any", status: "active" as "draft" | "active" | "archived" };

async function uploadImage(file: File): Promise<string> {
  const arrBuf = await file.arrayBuffer();
  const hashBuf = await crypto.subtle.digest("SHA-256", arrBuf);
  const sha256 = Array.from(new Uint8Array(hashBuf)).map(b => b.toString(16).padStart(2, "0")).join("");
  const initRes = await api.post<{ uploadId: string; uploadUrl: string }>(
    "/uploads/init", { kind: "image", mime: file.type, bytes: String(file.size) }
  );
  // Apache strips PUT bodies — use POST
  await fetch(initRes.uploadUrl, {
    method: "POST", body: new Blob([arrBuf]),
    headers: { "Content-Type": "application/octet-stream" }, credentials: "include",
  });
  await api.post(`/uploads/${initRes.uploadId}/complete`, { sha256 });
  return initRes.uploadId;
}

export function CatalogPage() {
  const { role } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [productImages, setProductImages] = useState<Record<string, ProductImage>>({});
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [formFile, setFormFile] = useState<File | null>(null);
  const [formFilePreview, setFormFilePreview] = useState<string | null>(null);
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Detail panel
  const [selected, setSelected] = useState<Product | null>(null);
  const [links, setLinks] = useState<AffiliateLink[]>([]);
  const [loadingLinks, setLoadingLinks] = useState(false);

  // Available influencer tags for selector
  const [availableTags, setAvailableTags] = useState<string[]>([]);

  // Edit product
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ title: "", category: "ropa", targetGender: "unisex", sourceUrl: "", targetTags: [] as string[], tagMatchMode: "any" as "all" | "any", status: "draft" as "draft" | "active" | "archived" });
  const [editError, setEditError] = useState("");
  const [editSubmitting, setEditSubmitting] = useState(false);

  // Delete product
  const [deleteError, setDeleteError] = useState("");
  const [deleting, setDeleting] = useState(false);

  // Assign
  const [influencers, setInfluencers] = useState<Influencer[]>([]);
  const [showAssign, setShowAssign] = useState(false);
  const [assignSearch, setAssignSearch] = useState("");
  const [assignTag, setAssignTag] = useState("");
  const [assignInf, setAssignInf] = useState("");
  const [assignUrl, setAssignUrl] = useState("");
  const [assignError, setAssignError] = useState("");

  function loadProducts() {
    setLoading(true);
    api.get<{ products: Product[] }>("/products?limit=50")
      .then(r => {
        const list = r.products ?? [];
        setProducts(list);
        // Load first asset for each product
        list.forEach(p => loadProductImage(p.id));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  async function loadProductImage(productId: string) {
    try {
      const detail = await api.get<{ assetRefs: { assetId: string }[] }>(`/products/${productId}`);
      const firstRef = detail.assetRefs?.[0];
      if (!firstRef) return;
      const r = await api.post<{ urls: Record<string, string> }>("/assets/batch-urls", { assetIds: [firstRef.assetId] });
      const url = r.urls?.[firstRef.assetId];
      if (url) setProductImages(prev => ({ ...prev, [productId]: { assetId: firstRef.assetId, downloadUrl: url } }));
    } catch { /* no image yet */ }
  }

  useEffect(() => {
    loadProducts();
    api.get<{ tags: string[] }>("/influencers/tags").then(r => setAvailableTags(r.tags ?? [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!influencers.length) {
      api.get<{ influencers: Influencer[] }>("/influencers?limit=200&status=active")
        .then(r => {
          const list = r.influencers ?? [];
          setInfluencers(list);
          if (list[0]) setAssignInf(list[0].id);
        }).catch(() => {});
    }
  }, [influencers.length]);

  function openDetail(p: Product) {
    setSelected(p);
    setEditing(false);
    setEditError("");
    setDeleteError("");
    setShowAssign(false);
    setAssignError("");
    setAssignSearch("");
    setAssignTag("");
    setLoadingLinks(true);
    api.get<{ links: AffiliateLink[] }>(`/products/${p.id}/affiliate-links`)
      .then(r => setLinks(r.links ?? []))
      .catch(() => setLinks([]))
      .finally(() => setLoadingLinks(false));
  }

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    if (!form.title.trim()) { setFormError("El nombre es obligatorio"); return; }
    setFormError("");
    setSubmitting(true);
    try {
      let assetRefs: { assetId: string; role: string; displayOrder: number }[] = [];
      if (formFile) {
        const assetId = await uploadImage(formFile);
        assetRefs = [{ assetId, role: "main", displayOrder: 0 }];
      }
      await api.post("/products", {
        title: form.title.trim(),
        category: form.category,
        targetGender: form.targetGender,
        targetTags: form.targetTags,
        tagMatchMode: form.tagMatchMode,
        status: form.status,
        sourceUrl: form.sourceUrl || undefined,
        assetRefs,
      });
      setCreating(false);
      setForm({ ...emptyForm });
      setFormFile(null);
      setFormFilePreview(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      loadProducts();
    } catch (err: unknown) {
      const e = err as { data?: { message?: string } };
      setFormError(e.data?.message || "Error al crear el producto");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleEdit(e: FormEvent) {
    e.preventDefault();
    if (!selected) return;
    setEditError("");
    setEditSubmitting(true);
    try {
      const updated = await api.patch<Product>(`/products/${selected.id}`, {
        title: editForm.title.trim(),
        targetGender: editForm.targetGender,
        targetTags: editForm.targetTags,
        tagMatchMode: editForm.tagMatchMode,
        status: editForm.status,
        sourceUrl: editForm.sourceUrl || null,
      }, { "If-Match": String(selected.revision) });
      setSelected(updated);
      setProducts(prev => prev.map(p => p.id === updated.id ? updated : p));
      setEditing(false);
    } catch (err: unknown) {
      const e = err as { data?: { message?: string } };
      setEditError(e.data?.message || "Error al guardar");
    } finally { setEditSubmitting(false); }
  }

  async function handleDelete() {
    if (!selected) return;
    setDeleteError("");
    setDeleting(true);
    try {
      await api.delete(`/products/${selected.id}`);
      setProducts(prev => prev.filter(p => p.id !== selected.id));
      setProductImages(prev => { const n = { ...prev }; delete n[selected.id]; return n; });
      setSelected(null);
    } catch (err: unknown) {
      const e = err as { data?: { message?: string; code?: string } };
      if (e.data?.code === "PRODUCT_IN_USE") {
        setDeleteError("No se puede borrar: el producto está asignado a influencers. Elimina primero las asignaciones.");
      } else {
        setDeleteError(e.data?.message || "Error al borrar");
      }
    } finally { setDeleting(false); }
  }

  // Filtered influencer list for assign panel
  const allTags = [...new Set(influencers.flatMap(i => i.tags))].sort();
  const filteredInfluencers = influencers.filter(i => {
    const matchName = !assignSearch || i.name.toLowerCase().includes(assignSearch.toLowerCase());
    const matchTag = !assignTag || i.tags.includes(assignTag);
    return matchName && matchTag;
  });

  async function handleAssign() {
    if (!selected || !assignInf) return;
    setAssignError("");
    try {
      await api.post("/affiliate-links", {
        product_id: selected.id,
        influencer_id: assignInf,
        affiliate_url: assignUrl || undefined,
        provider: "manual",
        market: "US",
        currency: "USD",
        link_source: "manual",
      });
      setShowAssign(false);
      setAssignUrl("");
      openDetail(selected);
    } catch (err: unknown) {
      const e = err as { data?: { message?: string } };
      setAssignError(e.data?.message || "Error al asignar");
    }
  }

  async function handleRemoveLink(linkId: string) {
    if (!selected) return;
    try {
      await api.delete(`/affiliate-links/${linkId}`);
      setLinks(prev => prev.filter(l => l.id !== linkId));
    } catch { /* ignore */ }
  }

  const canEdit = role === "owner" || role === "editor";

  if (loading) return <div style={{ padding: 24, color: "#888" }}>Cargando…</div>;

  return (
    <div style={{ display: "flex", gap: 24, alignItems: "flex-start" }}>
      {/* Product list */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Catálogo</h1>
            <p style={{ color: "#666", fontSize: 13, margin: "4px 0 0" }}>{products.length} productos</p>
          </div>
          {canEdit && (
            <button onClick={() => { setCreating(!creating); setFormError(""); }} style={btnPrim}>
              + Añadir producto
            </button>
          )}
        </div>

        {/* Create form */}
        {creating && (
          <form onSubmit={handleCreate} style={{ background: "#fff", padding: 24, borderRadius: 10, marginBottom: 24, display: "flex", flexDirection: "column", gap: 14 }}>
            <h3 style={{ margin: 0, fontSize: 16 }}>Nuevo producto</h3>
            {formError && <div style={{ color: "#dc2626", fontSize: 13 }}>{formError}</div>}

            {/* Image upload */}
            <div>
              <div style={{ fontSize: 13, color: "#444", fontWeight: 500, marginBottom: 6 }}>Imagen del producto</div>
              <label style={{ display: "block", cursor: "pointer" }}>
                {formFilePreview ? (
                  <div style={{ position: "relative", display: "inline-block" }}>
                    <img src={formFilePreview} alt="Preview" style={{ maxHeight: 160, maxWidth: "100%", borderRadius: 8, objectFit: "contain", border: "2px solid #2d6a4f", display: "block" }} />
                    <button type="button" onClick={e => { e.preventDefault(); setFormFile(null); setFormFilePreview(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                      style={{ position: "absolute", top: 6, right: 6, background: "rgba(0,0,0,0.5)", border: "none", borderRadius: "50%", width: 22, height: 22, cursor: "pointer", color: "#fff", fontSize: 13, lineHeight: "22px", textAlign: "center", padding: 0 }}>×</button>
                  </div>
                ) : (
                  <div style={{ border: "2px dashed #d1d5db", borderRadius: 8, padding: "24px 16px", textAlign: "center", color: "#aaa" }}>
                    <div style={{ fontSize: 24, marginBottom: 6 }}>📷</div>
                    <div style={{ fontSize: 12 }}>Haz clic para subir una imagen (JPG, PNG, WebP · max 20 MB)</div>
                  </div>
                )}
                <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/avif" style={{ display: "none" }}
                  onChange={e => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    setFormFile(f);
                    setFormFilePreview(URL.createObjectURL(f));
                  }}
                />
              </label>
            </div>

            <label style={lbl}>
              Nombre del producto *
              <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required style={inp} placeholder="Green midi dress" />
            </label>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <label style={lbl}>
                Categoría
                <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} style={inp}>
                  {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                </select>
              </label>
              <label style={lbl}>
                Para quién
                <select value={form.targetGender} onChange={e => setForm({ ...form, targetGender: e.target.value })} style={inp}>
                  <option value="female">Mujer</option>
                  <option value="male">Hombre</option>
                  <option value="unisex">Unisex</option>
                </select>
              </label>
              <label style={lbl}>
                Estado
                <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value as "draft" | "active" | "archived" })} style={inp}>
                  <option value="draft">Borrador</option>
                  <option value="active">Activo</option>
                  <option value="archived">Archivado</option>
                </select>
              </label>
            </div>

            {availableTags.length > 0 && (
              <div>
                <div style={{ fontSize: 13, color: "#444", fontWeight: 500, marginBottom: 6 }}>
                  Influencers objetivo (tags)
                  <span style={{ fontSize: 11, color: "#888", fontWeight: 400, marginLeft: 8 }}>— vacío = solo asignación manual</span>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                  {availableTags.map(tag => {
                    const active = form.targetTags.includes(tag);
                    return (
                      <button key={tag} type="button"
                        onClick={() => setForm(f => ({ ...f, targetTags: active ? f.targetTags.filter(t => t !== tag) : [...f.targetTags, tag] }))}
                        style={{ padding: "3px 10px", borderRadius: 12, border: `1px solid ${active ? "#2d6a4f" : "#d1d5db"}`, background: active ? "#d8f3dc" : "#f9fafb", color: active ? "#2d6a4f" : "#555", fontSize: 12, cursor: "pointer", fontWeight: active ? 600 : 400 }}>
                        {tag}
                      </button>
                    );
                  })}
                </div>
                {form.targetTags.length > 1 && (
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <span style={{ fontSize: 12, color: "#555" }}>Condición:</span>
                    <button type="button" onClick={() => setForm(f => ({ ...f, tagMatchMode: "any" }))}
                      style={{ padding: "2px 10px", borderRadius: 10, border: "1px solid", borderColor: form.tagMatchMode === "any" ? "#2d6a4f" : "#d1d5db", background: form.tagMatchMode === "any" ? "#d8f3dc" : "#f9fafb", color: form.tagMatchMode === "any" ? "#2d6a4f" : "#555", fontSize: 12, cursor: "pointer" }}>
                      Cumple alguna (OR)
                    </button>
                    <button type="button" onClick={() => setForm(f => ({ ...f, tagMatchMode: "all" }))}
                      style={{ padding: "2px 10px", borderRadius: 10, border: "1px solid", borderColor: form.tagMatchMode === "all" ? "#1d4ed8" : "#d1d5db", background: form.tagMatchMode === "all" ? "#dbeafe" : "#f9fafb", color: form.tagMatchMode === "all" ? "#1d4ed8" : "#555", fontSize: 12, cursor: "pointer" }}>
                      Cumple todas (AND)
                    </button>
                  </div>
                )}
              </div>
            )}

            <label style={lbl}>
              URL de afiliado
              <input value={form.sourceUrl} onChange={e => setForm({ ...form, sourceUrl: e.target.value })} style={inp}
                placeholder="https://www.amazon.com/dp/...?tag=..." type="url" />
            </label>

            <div style={{ display: "flex", gap: 8 }}>
              <button type="submit" disabled={submitting} style={{ ...btnPrim, opacity: submitting ? 0.7 : 1 }}>
                {submitting ? "Creando…" : "Crear"}
              </button>
              <button type="button" onClick={() => { setCreating(false); setFormFile(null); setFormFilePreview(null); }} style={btnSec}>Cancelar</button>
            </div>
          </form>
        )}

        {products.length === 0 ? (
          <div style={{ background: "#fff", borderRadius: 10, padding: 40, textAlign: "center", color: "#888" }}>
            Catálogo vacío. Añade productos para incluirlos en ideas de contenido.
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 12 }}>
            {products.map(p => {
              const img = productImages[p.id];
              return (
                <div
                  key={p.id}
                  onClick={() => openDetail(p)}
                  style={{
                    background: "#fff", borderRadius: 10, cursor: "pointer", overflow: "hidden",
                    border: selected?.id === p.id ? "2px solid #2d6a4f" : "2px solid transparent",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                  }}
                >
                  {/* Product image */}
                  <div style={{ height: 160, background: "#f5f4f0", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                    {img ? (
                      <img src={img.downloadUrl} alt={p.title} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                    ) : (
                      <div style={{ fontSize: 32, color: "#ccc" }}>📦</div>
                    )}
                  </div>
                  <div style={{ padding: "10px 12px" }}>
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.title}</div>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 12, background: GENDER_COLOR[p.targetGender] + "22", color: GENDER_COLOR[p.targetGender], fontWeight: 600 }}>
                        {GENDER_LABEL[p.targetGender] ?? p.targetGender}
                      </span>
                      <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 12, background: "#f0ece4", color: "#666" }}>
                        {p.category}
                      </span>
                      <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 12, fontWeight: 600,
                        background: p.status === "active" ? "#d8f3dc" : p.status === "draft" ? "#fef3c7" : "#f3f4f6",
                        color: p.status === "active" ? "#2d6a4f" : p.status === "draft" ? "#b45309" : "#6b7280" }}>
                        {p.status === "active" ? "Activo" : p.status === "draft" ? "Borrador" : "Archivado"}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail panel */}
      {selected && (
        <div style={{ width: 300, flexShrink: 0, background: "#fff", borderRadius: 10, overflow: "hidden", position: "sticky", top: 24, boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>
          {/* Product image */}
          {productImages[selected.id] ? (
            <img src={productImages[selected.id]!.downloadUrl} alt={selected.title} style={{ width: "100%", height: 200, objectFit: "contain", display: "block", background: "#f5f4f0" }} />
          ) : (
            <div style={{ width: "100%", height: 120, background: "#f5f4f0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 32, color: "#ccc" }}>📦</div>
          )}

          <div style={{ padding: 18 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, flex: 1 }}>{selected.title}</h3>
              <button onClick={() => setSelected(null)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "#aaa", lineHeight: 1, marginLeft: 8 }}>×</button>
            </div>

            {/* Edit / Delete actions */}
            {canEdit && !editing && (
              <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
                <button onClick={() => {
                  setEditForm({ title: selected.title, category: selected.category, targetGender: selected.targetGender, sourceUrl: selected.sourceUrl ?? "", targetTags: selected.targetTags ?? [], tagMatchMode: selected.tagMatchMode ?? "any", status: selected.status as "draft" | "active" | "archived" });
                  setEditing(true); setEditError("");
                }} style={{ ...btnMini, background: "#f0ece4", color: "#444", padding: "4px 10px" }}>Editar</button>
                <button onClick={() => {
                  if (window.confirm(`¿Borrar "${selected.title}"? Esta acción no se puede deshacer.`)) handleDelete();
                }} disabled={deleting} style={{ ...btnMini, background: "#fee2e2", color: "#dc2626", padding: "4px 10px" }}>
                  {deleting ? "Borrando…" : "Borrar"}
                </button>
              </div>
            )}
            {deleteError && <div style={{ fontSize: 12, color: "#dc2626", marginBottom: 10, padding: "6px 8px", background: "#fee2e2", borderRadius: 6 }}>{deleteError}</div>}

            {/* Inline edit form */}
            {editing ? (
              <form onSubmit={handleEdit} style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
                {editError && <div style={{ fontSize: 12, color: "#dc2626" }}>{editError}</div>}
                <label style={lbl}>Nombre<input value={editForm.title} onChange={e => setEditForm({ ...editForm, title: e.target.value })} required style={{ ...inp, fontSize: 12 }} /></label>
                <label style={lbl}>Para quién
                  <select value={editForm.targetGender} onChange={e => setEditForm({ ...editForm, targetGender: e.target.value })} style={{ ...inp, fontSize: 12 }}>
                    <option value="female">Mujer</option><option value="male">Hombre</option><option value="unisex">Unisex</option>
                  </select>
                </label>
                <label style={lbl}>Estado
                  <select value={editForm.status} onChange={e => setEditForm({ ...editForm, status: e.target.value as "draft" | "active" | "archived" })} style={{ ...inp, fontSize: 12 }}>
                    <option value="draft">Borrador</option>
                    <option value="active">Activo</option>
                    <option value="archived">Archivado</option>
                  </select>
                </label>
                {availableTags.length > 0 && (
                  <div>
                    <div style={{ fontSize: 12, color: "#444", fontWeight: 500, marginBottom: 4 }}>Influencers objetivo (tags)</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 6 }}>
                      {availableTags.map(tag => {
                        const active = editForm.targetTags.includes(tag);
                        return (
                          <button key={tag} type="button"
                            onClick={() => setEditForm(f => ({ ...f, targetTags: active ? f.targetTags.filter(t => t !== tag) : [...f.targetTags, tag] }))}
                            style={{ padding: "2px 8px", borderRadius: 10, border: `1px solid ${active ? "#2d6a4f" : "#d1d5db"}`, background: active ? "#d8f3dc" : "#f9fafb", color: active ? "#2d6a4f" : "#555", fontSize: 11, cursor: "pointer", fontWeight: active ? 600 : 400 }}>
                            {tag}
                          </button>
                        );
                      })}
                    </div>
                    {editForm.targetTags.length > 1 && (
                      <div style={{ display: "flex", gap: 6, marginBottom: 4 }}>
                        <button type="button" onClick={() => setEditForm(f => ({ ...f, tagMatchMode: "any" }))}
                          style={{ padding: "2px 8px", borderRadius: 8, border: "1px solid", borderColor: editForm.tagMatchMode === "any" ? "#2d6a4f" : "#d1d5db", background: editForm.tagMatchMode === "any" ? "#d8f3dc" : "#f9fafb", color: editForm.tagMatchMode === "any" ? "#2d6a4f" : "#555", fontSize: 11, cursor: "pointer" }}>OR (alguna)</button>
                        <button type="button" onClick={() => setEditForm(f => ({ ...f, tagMatchMode: "all" }))}
                          style={{ padding: "2px 8px", borderRadius: 8, border: "1px solid", borderColor: editForm.tagMatchMode === "all" ? "#1d4ed8" : "#d1d5db", background: editForm.tagMatchMode === "all" ? "#dbeafe" : "#f9fafb", color: editForm.tagMatchMode === "all" ? "#1d4ed8" : "#555", fontSize: 11, cursor: "pointer" }}>AND (todas)</button>
                      </div>
                    )}
                  </div>
                )}
                <label style={lbl}>URL de afiliado<input value={editForm.sourceUrl} onChange={e => setEditForm({ ...editForm, sourceUrl: e.target.value })} style={{ ...inp, fontSize: 12 }} type="url" placeholder="https://…" /></label>
                <div style={{ display: "flex", gap: 6 }}>
                  <button type="submit" disabled={editSubmitting} style={{ ...btnMini, background: "#2d6a4f", color: "#fff", padding: "5px 12px" }}>{editSubmitting ? "…" : "Guardar"}</button>
                  <button type="button" onClick={() => setEditing(false)} style={{ ...btnMini, background: "#f0ece4", color: "#444", padding: "5px 12px" }}>Cancelar</button>
                </div>
              </form>
            ) : (
              <>
                <div style={{ display: "flex", gap: 6, marginBottom: 6, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 12, background: GENDER_COLOR[selected.targetGender] + "22", color: GENDER_COLOR[selected.targetGender], fontWeight: 600 }}>
                    {GENDER_LABEL[selected.targetGender] ?? selected.targetGender}
                  </span>
                  <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 12, background: "#f0ece4", color: "#666" }}>{selected.category}</span>
                </div>

                {(selected.targetTags ?? []).length > 0 && (
                  <div style={{ marginBottom: 10 }}>
                    <span style={{ fontSize: 11, color: "#888" }}>Auto-asignado a influencers con {selected.tagMatchMode === "all" ? "todos" : "alguno de"} estos tags: </span>
                    {(selected.targetTags ?? []).map(t => (
                      <span key={t} style={{ fontSize: 11, padding: "1px 7px", borderRadius: 10, background: "#d8f3dc", color: "#2d6a4f", marginLeft: 4, fontWeight: 600 }}>{t}</span>
                    ))}
                  </div>
                )}

                {selected.sourceUrl && (
                  <div style={{ marginBottom: 14 }}>
                    <a href={selected.sourceUrl} target="_blank" rel="noopener noreferrer"
                      style={{ fontSize: 12, color: "#2d6a4f", wordBreak: "break-all", display: "block", padding: "6px 10px", background: "#f0fdf4", borderRadius: 6, textDecoration: "none" }}>
                      🔗 {selected.sourceUrl.length > 50 ? selected.sourceUrl.slice(0, 50) + "…" : selected.sourceUrl}
                    </a>
                  </div>
                )}
              </>
            )}

            {/* Assigned influencers */}
            <div style={{ borderTop: "1px solid #f0ece4", paddingTop: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>Influencers ({links.length})</span>
                {canEdit && (
                  <button onClick={() => { setShowAssign(!showAssign); setAssignError(""); }} style={{ ...btnMini, background: "#d8f3dc", color: "#2d6a4f" }}>
                    + Asignar
                  </button>
                )}
              </div>

              {/* Assign panel */}
              {showAssign && (
                <div style={{ background: "#f8faf9", borderRadius: 8, padding: 12, marginBottom: 10, display: "flex", flexDirection: "column", gap: 8 }}>
                  {assignError && <div style={{ color: "#dc2626", fontSize: 12 }}>{assignError}</div>}

                  {/* Search by name */}
                  <input
                    value={assignSearch}
                    onChange={e => setAssignSearch(e.target.value)}
                    placeholder="Buscar influencer…"
                    style={{ ...inp, fontSize: 12 }}
                  />

                  {/* Filter by tag */}
                  {allTags.length > 0 && (
                    <select value={assignTag} onChange={e => setAssignTag(e.target.value)} style={{ ...inp, fontSize: 12 }}>
                      <option value="">Todos los tags</option>
                      {allTags.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  )}

                  {/* Influencer selector */}
                  <select value={assignInf} onChange={e => setAssignInf(e.target.value)} style={{ ...inp, fontSize: 12 }} size={Math.min(filteredInfluencers.length, 5)}>
                    {filteredInfluencers.map(i => (
                      <option key={i.id} value={i.id}>
                        {i.name}{i.tags.length ? ` · ${i.tags.slice(0, 2).join(", ")}` : ""}
                      </option>
                    ))}
                    {filteredInfluencers.length === 0 && <option disabled>Sin coincidencias</option>}
                  </select>

                  {/* Affiliate URL */}
                  <input
                    value={assignUrl}
                    onChange={e => setAssignUrl(e.target.value)}
                    placeholder="URL de afiliado (opcional)"
                    style={{ ...inp, fontSize: 12 }}
                    type="url"
                  />

                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={handleAssign} disabled={!assignInf} style={{ ...btnMini, background: "#2d6a4f", color: "#fff", padding: "5px 12px", opacity: !assignInf ? 0.5 : 1 }}>Guardar</button>
                    <button onClick={() => setShowAssign(false)} style={{ ...btnMini, background: "#f0ece4", color: "#444", padding: "5px 12px" }}>Cancelar</button>
                  </div>
                </div>
              )}

              {loadingLinks ? (
                <div style={{ fontSize: 12, color: "#aaa" }}>Cargando…</div>
              ) : links.length === 0 ? (
                <div style={{ fontSize: 12, color: "#aaa" }}>Sin influencers asignadas aún.</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {links.map(l => (
                    <div key={l.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, padding: "6px 8px", background: "#f9f7f4", borderRadius: 6 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600 }}>{l.influencer_name ?? "—"}</div>
                        {l.affiliate_url ? (
                          <a href={l.affiliate_url} target="_blank" rel="noopener noreferrer" style={{ color: "#2d6a4f", fontSize: 11, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {l.affiliate_url.slice(0, 35)}…
                          </a>
                        ) : <span style={{ color: "#aaa", fontSize: 11 }}>Sin URL</span>}
                      </div>
                      {canEdit && (
                        <button onClick={() => handleRemoveLink(l.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "#dc2626", fontSize: 14, padding: "2px 4px", marginLeft: 4 }}>×</button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const btnPrim: React.CSSProperties = { padding: "8px 16px", background: "#2d6a4f", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 14 };
const btnSec: React.CSSProperties = { padding: "8px 16px", border: "1px solid #ddd", borderRadius: 6, cursor: "pointer", background: "none", fontSize: 13 };
const btnMini: React.CSSProperties = { padding: "3px 8px", border: "none", borderRadius: 5, cursor: "pointer", fontSize: 11, fontWeight: 600 };
const inp: React.CSSProperties = { padding: "7px 10px", border: "1px solid #ddd", borderRadius: 6, fontSize: 13, outline: "none", width: "100%", boxSizing: "border-box" };
const lbl: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 4, fontSize: 13, color: "#444" };
